import React, { useEffect, useMemo, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../components/PageTitle'
import KpiTile from '../freight-profitability/KpiTile'
import {
  useGetStuckSummaryQuery,
  useRefreshStuckSnapshotMutation,
  useGetStuckRefreshStatusQuery,
} from '../../redux/stuckShipmentsApiSlice'
import StuckFilters from './StuckFilters'
import StuckGroupTable from './StuckGroupTable'
import StuckShipmentsTab from './StuckShipmentsTab'
import { BUCKETS, THRESHOLDS, ageLabel, count, downloadRowsAsCsv, groupLabel, inr } from './stuckUtils'

const TABS = ['By courier', 'By merchant', 'By status', 'Shipments']
const TABLE_COLS = (nameLabel) => [
  { key: 'name', label: nameLabel },
  ...BUCKETS.map(b => ({ key: b.rowKey, label: b.label })),
  { key: 'total', label: 'Total' },
  { key: 'breached', label: 'Past expected date' },
]

/**
 * Stuck Shipments — the Control Tower "Daily Alert – D2C / PTL Stuck Shipments / Courier Partner Wise" mail as a page.
 * A shipment is stuck when it is still open (pickup / delivery / RTO pending) and the courier has sent no status update
 * for the chosen threshold (48 h in the mail). Numbers come from an hourly snapshot; Refresh takes a new one.
 * Backend: /api/ops/stuck-shipments.
 */
export default function StuckShipments() {
  const { enqueueSnackbar } = useSnackbar()
  const [filters, setFilters] = useState({ segment: 'D2C', thresholdHours: 48, merchantOids: [], courierOids: [], statusGroups: [], breachedOnly: false })
  const [tab, setTab] = useState(0)
  const [bucket, setBucket] = useState(null)

  const scope = useMemo(() => ({
    segment: filters.segment, thresholdHours: filters.thresholdHours,
    merchantOids: filters.merchantOids, courierOids: filters.courierOids,
    statusGroups: filters.statusGroups, breachedOnly: filters.breachedOnly || undefined,
  }), [filters])

  const { data, isFetching, error, refetch } = useGetStuckSummaryQuery(scope)
  const s = data?.summary || {}
  const noSnapshot = data && !data.takenAt

  // Refresh = new capture on the backend (task framework); poll until done, then refetch.
  const [refresh, { isLoading: queueing }] = useRefreshStuckSnapshotMutation()
  const [refreshId, setRefreshId] = useState(null)
  const { data: status, error: pollError } = useGetStuckRefreshStatusQuery(refreshId, { skip: !refreshId, pollingInterval: 3000 })
  const pollErrors = useRef(0)
  useEffect(() => {
    if (!refreshId) { pollErrors.current = 0; return }
    if (pollError) {
      pollErrors.current += 1
      if (pollErrors.current >= 5) {
        setRefreshId(null)
        enqueueSnackbar('Lost track of the refresh. It may still be running — check STUCK_SNAPSHOT in the server logs and reload in a minute.', { variant: 'warning', autoHideDuration: 10000 })
      }
    } else if (status) pollErrors.current = 0
  }, [pollError, status, refreshId, enqueueSnackbar])
  useEffect(() => {
    if (!status || !refreshId) return
    if (status.state === 'DONE' || status.state === 'FAILED') {
      setRefreshId(null)
      if (status.state === 'FAILED') enqueueSnackbar(`Refresh failed: ${status.message || 'see server logs'}`, { variant: 'error', autoHideDuration: 10000 })
      else enqueueSnackbar(`Snapshot refreshed · ${count(status.rows)} stuck shipments captured`, { variant: 'success' })
      refetch()
    }
  }, [status, refreshId, enqueueSnackbar, refetch])

  const onRefresh = async () => {
    try {
      const res = await refresh().unwrap()
      setRefreshId(res.requestId)
      enqueueSnackbar('Taking a fresh snapshot of open shipments…', { variant: 'info' })
    } catch (e) {
      // 409 carries the reason: another refresh running, or the snapshot is younger than the minimum gap.
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not start a refresh', { variant: 'error', autoHideDuration: 8000 })
    }
  }
  const refreshing = queueing || Boolean(refreshId)

  const thresholdLabel = THRESHOLDS.find(t => t.value === filters.thresholdHours)?.label || `${filters.thresholdHours} h`
  const breachedShare = s.total ? Math.round((s.breached / s.total) * 100) : 0
  const openBucket = (key) => { setBucket(key); setTab(3) }
  const scopeTo = (patch) => { setFilters(f => ({ ...f, ...patch })); setTab(3) }

  const exportTable = () => {
    const src = tab === 0 ? { rows: data?.byCourier, name: 'Courier', file: 'COURIER_WISE' }
      : tab === 1 ? { rows: data?.byMerchant, name: 'Merchant', file: 'MERCHANT_WISE' }
      : { rows: (data?.byStatus || []).map(r => ({ ...r, name: `${r.name} (${groupLabel(r.group)})` })), name: 'Status', file: 'STATUS_WISE' }
    downloadRowsAsCsv(src.rows || [], TABLE_COLS(src.name), `STUCK_SHIPMENTS_${src.file}_${filters.segment}_${filters.thresholdHours}H.csv`)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageTitle>Stuck Shipments</PageTitle>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant='h5' sx={{ fontWeight: 700 }}>Stuck Shipments</Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: 900 }}>
            Open shipments (pickup, delivery or RTO pending) whose courier has sent no status update for {thresholdLabel}.
            Excludes test and external merchants and external couriers. Age counts from the last courier scan in the order history;
            "Past expected date" compares today with the EDD (pickup and delivery pending) or the RTO EDD (RTO pending).
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data?.takenAt && (
            <Typography variant='caption' sx={{ color: 'text.disabled' }} title={`Snapshot taken ${data.takenAt} IST`}>
              As of {data.takenAt} IST · {ageLabel(data.ageMinutes)}
            </Typography>
          )}
          <span title={refreshing ? 'A refresh is already running' : 'Take a fresh snapshot of open shipments now (hourly otherwise)'}>
            <Button size='small' variant='outlined' startIcon={<RefreshOutlinedIcon />} onClick={onRefresh} disabled={refreshing} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {refreshing ? 'Refreshing…' : 'Refresh snapshot'}
            </Button>
          </span>
        </Box>
      </Box>

      <StuckFilters filters={filters} onChange={(f) => { setFilters(f); setBucket(null) }} disabled={refreshing} />

      {error && <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load stuck shipments.'}</Alert>}

      {noSnapshot && !isFetching && (
        <Alert severity='warning' action={<Button size='small' color='inherit' onClick={onRefresh} disabled={refreshing}>Refresh now</Button>}>
          No snapshot has been taken yet. The hourly job fills it; refresh to capture one now.
        </Alert>
      )}

      {/* The mail's headline numbers: total and the four age buckets, plus what the mail could not tell you */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1.5 }}>
        <KpiTile title='Stuck shipments' value={count(s.total)} sub={`${count(s.merchants)} merchants · ${count(s.couriers)} couriers`} tone={s.total ? 'warning' : 'neutral'} loading={isFetching} onClick={() => { setBucket(null); setTab(3) }} />
        {BUCKETS.map(b => (
          <KpiTile key={b.key} title={b.label} value={count(s.byBucket?.[b.key])} sub='since last courier update' tone={b.key === 'D15_PLUS' && s.byBucket?.[b.key] ? 'loss' : 'neutral'} loading={isFetching} onClick={() => openBucket(b.key)} />
        ))}
        <KpiTile title='Past expected date' value={count(s.breached)} sub={`${breachedShare}% of stuck`} tone={s.breached ? 'loss' : 'neutral'} loading={isFetching} onClick={() => scopeTo({ breachedOnly: true })} />
        <KpiTile title='Invoice value stuck' value={inr(s.invoiceValue)} sub='customer value sitting still' loading={isFetching} />
      </Box>

      {!isFetching && data && filters.segment === 'PTL' && s.appointment > 0 && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip size='small' variant='outlined' label={`${count(s.appointment)} appointment-based deliveries among the stuck`} />
        </Box>
      )}

      <Paper variant='outlined' sx={{ px: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minWidth: 120 } }}>
            <Tab label={`${TABS[0]}${data ? ` · ${count(data.byCourier?.length)}` : ''}`} />
            <Tab label={`${TABS[1]}${data ? ` · ${count(data.byMerchant?.length)}` : ''}`} />
            <Tab label={`${TABS[2]}${data ? ` · ${count(data.byStatus?.length)}` : ''}`} />
            <Tab label={`${TABS[3]}${data ? ` · ${count(s.total)}` : ''}`} />
          </Tabs>
          {tab < 3 && (
            <Button size='small' startIcon={<DownloadOutlinedIcon />} onClick={exportTable} disabled={isFetching || !data} sx={{ textTransform: 'none', fontWeight: 600 }}>CSV</Button>
          )}
        </Box>

        <Box sx={{ pt: 1.5 }}>
          {tab === 0 && (
            <StuckGroupTable rows={data?.byCourier || []} nameLabel='Courier' loading={isFetching}
              onRowClick={(r) => scopeTo({ courierOids: [r.key] })} />
          )}
          {tab === 1 && (
            <StuckGroupTable rows={data?.byMerchant || []} nameLabel='Merchant' loading={isFetching}
              onRowClick={(r) => scopeTo({ merchantOids: [r.key] })} />
          )}
          {tab === 2 && (
            <StuckGroupTable rows={data?.byStatus || []} nameLabel='Status' loading={isFetching}
              renderName={(r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{r.name}</span>
                  <Chip size='small' variant='outlined' label={groupLabel(r.group)} sx={{ height: 18, fontSize: 10 }} />
                </Box>
              )}
              onRowClick={(r) => scopeTo({ statusGroups: [r.group] })} />
          )}
          {tab === 3 && <StuckShipmentsTab scope={scope} bucket={bucket} onClearBucket={() => setBucket(null)} />}
        </Box>
      </Paper>

      <Typography variant='caption' sx={{ color: 'text.disabled' }}>
        Tables are sorted busiest first, like the mail, with its Total row at the bottom. Click a row to see its shipments.
        The snapshot is refreshed hourly; a manual refresh is allowed every 10 minutes.
      </Typography>
    </Box>
  )
}
